/* eslint-disable */
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Spin } from "antd";
import {
  MessageCircle,
  Filter,
  User,
  Bot,
  Image as ImageIcon,
  Mic,
  Clock,
  Package,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
} from "lucide-react";
import { SERVER_URL } from "../../constants/server_url";
import { getAuth } from "firebase/auth";

// const API_URL = `${SERVER_URL.development}foorchat/chats`;
const API_URL = `${process.env.REACT_APP_BACKEND_DIGITALOCEAN_URL}/foorchat/chats`;

const CHATS_PER_PAGE = 10;
const MESSAGES_PER_LOAD = 10;
const MAX_VISIBLE_PAGES = 5;

const FoorchatChats = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  const products = useSelector((state) => state.ProductsReducer || []);

  console.log("products");
  console.log(products);
  const productsWithAds = useMemo(() => {
    return (products || []).filter(
      (p) => p.deleted !== 1 && Array.isArray(p.adsIds) && p.adsIds.length > 0,
    );
  }, [products]);

  console.log("productsWithAds");
  console.log(productsWithAds);

  const productIdToProduct = useMemo(() => {
    const map = {};
    productsWithAds.forEach((p) => {
      if (p.id) map[p.id] = p;
    });
    return map;
  }, [productsWithAds]);

  const adsIdToProduct = useMemo(() => {
    const map = {};
    productsWithAds.forEach((p) => {
      if (Array.isArray(p.adsIds)) {
        p.adsIds.forEach((id) => {
          if (id) map[id] = p;
        });
      }
    });
    return map;
  }, [productsWithAds]);

  const adsIdsToFetch = useMemo(() => {
    const ids = new Set();
    productsWithAds.forEach((p) => {
      if (Array.isArray(p.adsIds)) {
        p.adsIds.forEach((id) => {
          if (id && typeof id === "string" && id.trim()) {
            ids.add(id.trim());
          }
        });
      }
    });
    return Array.from(ids);
  }, [productsWithAds]);

  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const [selectedProductId, setSelectedProductId] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  const [activeChatId, setActiveChatId] = useState(null);
  const [visibleMessagesCount, setVisibleMessagesCount] =
    useState(MESSAGES_PER_LOAD);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const fetchChats = useCallback(
    async (isRefresh = false) => {
      if (!adsIdsToFetch.length) {
        setChats([]);
        setLoading(false);
        setRefreshing(false);
        setError(null);
        return;
      }

      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);

      try {
        const user = getAuth().currentUser;
        if (!user) {
          throw new Error("Not authenticated");
        }
        const token = await user.getIdToken();

        const params = new URLSearchParams();
        params.set("adsIds", adsIdsToFetch.join(","));

        const res = await fetch(`${API_URL}?${params.toString()}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "ngrok-skip-browser-warning": "true",
          },
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();
        const list = Array.isArray(data) ? data : data.chats || data.data || [];

        list.sort((a, b) => {
          const da = new Date(a.updatedAt || a.createdAt || 0).getTime();
          const db = new Date(b.updatedAt || b.createdAt || 0).getTime();
          return db - da;
        });

        setChats(list);
        setCurrentPage(1);
      } catch (err) {
        console.error("Error fetching chats:", err);
        setError(err.message || "Failed to load chats");
        setChats([]);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [adsIdsToFetch],
  );

  useEffect(() => {
    fetchChats();
  }, [fetchChats]);

  const filterAdsIds = useMemo(() => {
    const ids = new Set();

    chats.forEach((c) => {
      if (c.adsId) ids.add(c.adsId);
    });

    if (ids.size === 0) {
      chats.forEach((c) => {
        if (c.productId && productIdToProduct[c.productId]) {
          const product = productIdToProduct[c.productId];
          product.adsIds?.forEach((id) => ids.add(id));
        }
      });
    }

    return Array.from(ids).sort();
  }, [chats, productIdToProduct]);

  const filteredChats = useMemo(() => {
    if (selectedProductId === "all") return chats;

    return chats.filter((c) => c.adsId === selectedProductId);
  }, [chats, selectedProductId]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredChats.length / CHATS_PER_PAGE),
  );

  const paginatedChats = useMemo(() => {
    const start = (currentPage - 1) * CHATS_PER_PAGE;
    return filteredChats.slice(start, start + CHATS_PER_PAGE);
  }, [filteredChats, currentPage]);

  const activeChat = useMemo(() => {
    if (!activeChatId) return null;
    return chats.find((c) => (c._id || c.id) === activeChatId) || null;
  }, [chats, activeChatId]);

  const visibleMessages = useMemo(() => {
    if (!activeChat?.messages?.length) return [];
    const total = activeChat.messages.length;
    const start = Math.max(0, total - visibleMessagesCount);
    return activeChat.messages.slice(start);
  }, [activeChat, visibleMessagesCount]);

  useEffect(() => {
    if (activeChatId) {
      setVisibleMessagesCount(MESSAGES_PER_LOAD);
    }
  }, [activeChatId]);

  useEffect(() => {
    if (messagesEndRef.current && visibleMessagesCount <= MESSAGES_PER_LOAD) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [activeChatId, visibleMessagesCount]);

  const getVisiblePageNumbers = () => {
    if (totalPages <= MAX_VISIBLE_PAGES) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    let start = Math.max(1, currentPage - Math.floor(MAX_VISIBLE_PAGES / 2));
    let end = start + MAX_VISIBLE_PAGES - 1;

    if (end > totalPages) {
      end = totalPages;
      start = end - MAX_VISIBLE_PAGES + 1;
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const handleMessagesScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container || loadingMoreMessages || !activeChat) return;

    if (container.scrollTop < 40) {
      const total = activeChat.messages?.length || 0;
      if (visibleMessagesCount >= total) return;

      setLoadingMoreMessages(true);

      const prevHeight = container.scrollHeight;

      setTimeout(() => {
        setVisibleMessagesCount((prev) =>
          Math.min(prev + MESSAGES_PER_LOAD, total),
        );
        requestAnimationFrame(() => {
          const newHeight = container.scrollHeight;
          container.scrollTop = newHeight - prevHeight;
          setLoadingMoreMessages(false);
        });
      }, 280);
    }
  }, [activeChat, visibleMessagesCount, loadingMoreMessages]);

  const getChatId = (chat) => chat._id || chat.id;

  const getMessageText = (msg) => {
    if (!msg) return "";
    if (typeof msg.text === "string") return msg.text;
    if (msg.text && typeof msg.text === "object") return msg.text.reply || "";
    return "";
  };

  const getLastMessagePreview = (chat) => {
    if (!chat?.messages?.length) return t("foorchat.chats.no_messages");
    const last = chat.messages[chat.messages.length - 1];
    if (last.type === "audio") return `🎤 ${t("foorchat.chats.audio_message")}`;
    const text = getMessageText(last);
    return text.length > 60 ? text.slice(0, 60) + "…" : text;
  };

  const toDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  };

  const formatTimestamp = (ts) => {
    const date = toDate(ts);
    if (!date) return "";
    try {
      const now = new Date();
      const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        return date.toLocaleTimeString(i18n.language, {
          hour: "2-digit",
          minute: "2-digit",
        });
      }
      if (diffDays === 1) return t("foorchat.chats.yesterday");
      if (diffDays < 7) {
        return date.toLocaleDateString(i18n.language, { weekday: "short" });
      }
      return date.toLocaleDateString(i18n.language, {
        day: "numeric",
        month: "short",
      });
    } catch {
      return "";
    }
  };

  const formatFullTimestamp = (ts) => {
    const date = toDate(ts);
    if (!date) return "";
    try {
      return date.toLocaleString(i18n.language, {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  const getMessageRole = (msg) => {
    const role = (msg.role || "").toLowerCase();
    if (role === "bot") return "bot";
    if (role === "owner") return "owner";
    return "user";
  };

  const getProductName = (chat) => {
    if (!chat) return t("foorchat.chats.product");

    // try by productId first
    if (chat.productId && productIdToProduct[chat.productId]) {
      return productIdToProduct[chat.productId].name || chat.productId;
    }

    // try by adsId
    if (chat.adsId && adsIdToProduct[chat.adsId]) {
      return adsIdToProduct[chat.adsId].name || chat.adsId;
    }

    return chat.productId || t("foorchat.chats.product");
  };

  const VoicePlayer = ({ url, isUser }) => {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);

    const togglePlay = () => {
      const audio = audioRef.current;
      if (!audio) return;

      if (isPlaying) {
        audio.pause();
      } else {
        audio.play();
      }
      setIsPlaying(!isPlaying);
    };

    const handleTimeUpdate = () => {
      const audio = audioRef.current;
      if (!audio) return;
      setProgress(audio.currentTime);
    };

    const handleLoaded = () => {
      const audio = audioRef.current;
      if (!audio) return;
      setDuration(audio.duration || 0);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setProgress(0);
    };

    const handleSeek = (e) => {
      const audio = audioRef.current;
      if (!audio) return;
      const value = Number(e.target.value);
      audio.currentTime = value;
      setProgress(value);
    };

    const formatTime = (sec) => {
      if (!sec || isNaN(sec)) return "0:00";
      const m = Math.floor(sec / 60);
      const s = Math.floor(sec % 60);
      return `${m}:${s.toString().padStart(2, "0")}`;
    };

    return (
      <div className="flex items-center gap-2 min-w-[200px] max-w-[260px]">
        <audio
          ref={audioRef}
          src={url}
          preload="metadata"
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoaded}
          onEnded={handleEnded}
        />

        <button
          type="button"
          onClick={togglePlay}
          className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition ${
            isUser
              ? "bg-white/20 hover:bg-white/30 text-white"
              : "bg-[#9e68ba]/15 hover:bg-[#9e68ba]/25 text-[#9e68ba]"
          }`}
        >
          {isPlaying ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="4" width="4" height="16" rx="1" />
              <rect x="14" y="4" width="4" height="16" rx="1" />
            </svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>

        <div className="flex-1 flex flex-col gap-0.5">
          <input
            type="range"
            min={0}
            max={duration || 100}
            value={progress}
            onChange={handleSeek}
            className="w-full h-1 rounded-full appearance-none cursor-pointer
              [&::-webkit-slider-thumb]:appearance-none
              [&::-webkit-slider-thumb]:w-0
              [&::-webkit-slider-thumb]:h-0
              [&::-moz-range-thumb]:appearance-none
              [&::-moz-range-thumb]:w-0
              [&::-moz-range-thumb]:h-0
              [&::-ms-thumb]:appearance-none
              [&::-ms-thumb]:w-0
              [&::-ms-thumb]:h-0"
            style={{
              background: isUser
                ? `linear-gradient(to right, #fff ${(progress / (duration || 1)) * 100}%, rgba(255,255,255,0.3) ${(progress / (duration || 1)) * 100}%)`
                : `linear-gradient(to right, #9e68ba ${(progress / (duration || 1)) * 100}%, #e5e7eb ${(progress / (duration || 1)) * 100}%)`,
            }}
          />
          <div
            className={`flex justify-between text-[10px] ${
              isUser ? "text-white/70" : "text-gray-400"
            }`}
          >
            <span>{formatTime(progress)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="m-3 flex items-center justify-center min-h-[60vh]">
        <div className="w-full max-w-md text-center px-6 py-10 bg-white dark:!bg-[#181819] rounded-2xl shadow-sm border border-[#fbf4ff] dark:!border-[#3c3a3d]">
          <div className="mx-auto mb-5 w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
            <MessageCircle
              size={28}
              className="text-red-500 dark:text-red-400"
            />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">
            {t("foorchat.chats.error_title")}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 leading-relaxed">
            {t("foorchat.chats.error_message")}
          </p>
          <button
            onClick={() => fetchChats()}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#9e68ba] text-white text-sm font-medium hover:bg-[#8a5aa5] active:scale-[0.98] transition-all shadow-sm"
          >
            <RefreshCw size={16} />
            {t("foorchat.chats.retry")}
          </button>
        </div>
      </div>
    );
  }

  if (activeChat) {
    const totalMessages = activeChat.messages?.length || 0;
    const hasMoreMessages = visibleMessagesCount < totalMessages;

    return (
      <div className="m-3 flex flex-col h-[calc(100vh-220px)] min-h-[500px] bg-white dark:!bg-[#181819] rounded-xl shadow-sm border border-[#fbf4ff] dark:!border-[#3c3a3d] overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 dark:border-[#3c3a3d] bg-[#fcf6ff] dark:bg-[#1f1e20]">
          <button
            onClick={() => setActiveChatId(null)}
            className="p-2 rounded-lg hover:bg-purple-100 dark:hover:bg-[#6e358c]/30 transition-colors"
            aria-label={t("foorchat.chats.back")}
          >
            <ChevronLeft
              size={20}
              className={`text-[#9e68ba] ${isRTL ? "rotate-180" : ""}`}
            />
          </button>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Package size={16} className="text-[#9e68ba] flex-shrink-0" />
              <span className="font-semibold text-gray-800 dark:text-gray-200 truncate">
                {getProductName(activeChat)}
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {activeChat.userName || t("foorchat.chats.facebook_user")} ·{" "}
              {formatFullTimestamp(activeChat.updatedAt)} · {totalMessages}{" "}
              {t("foorchat.chats.messages")}
            </p>
          </div>

          <button
            onClick={() => fetchChats(true)}
            disabled={refreshing}
            className="p-2 rounded-lg hover:bg-purple-100 dark:hover:bg-[#6e358c]/30 transition-colors"
            title={t("foorchat.chats.refresh")}
          >
            <RefreshCw
              size={16}
              className={`text-[#9e68ba] ${refreshing ? "animate-spin" : ""}`}
            />
          </button>
        </div>

        <div
          ref={messagesContainerRef}
          onScroll={handleMessagesScroll}
          className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50/50 dark:bg-[#141414]"
        >
          {hasMoreMessages && (
            <div className="flex justify-center py-2">
              {loadingMoreMessages ? (
                <Spin size="small" />
              ) : (
                <span className="text-xs text-gray-400">
                  {t("foorchat.chats.scroll_for_more")}
                </span>
              )}
            </div>
          )}

          {visibleMessages.map((msg, idx) => {
            const role = getMessageRole(msg);
            const isLeftSide = role === "bot" || role === "owner";
            const text = getMessageText(msg);
            const isAudio =
              msg.type === "voice" ||
              (typeof msg.text === "string" && msg.text.trim() === "[audio]");
            const isImage =
              msg.type === "image" ||
              (typeof msg.text === "string" && msg.text.trim() === "[image]");
            const extra =
              role === "bot" && typeof msg.text === "object" ? msg.text : null;

            return (
              <div
                key={`${msg.timestamp || idx}-${idx}`}
                className={`flex ${isLeftSide ? "justify-start" : "justify-end"}`}
              >
                <div
                  className={`max-w-[75%] rounded-2xl px-4 py-2.5 shadow-sm ${
                    role === "bot"
                      ? "bg-white dark:!bg-[#222224] border border-gray-100 dark:!border-[#3c3a3d] text-gray-800 dark:!text-gray-200 rounded-tl-sm"
                      : role === "owner"
                        ? "bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40 text-gray-800 dark:text-gray-200 rounded-tl-sm"
                        : "bg-gradient-to-r from-[#9e68ba] to-[#bc7ade] text-white rounded-tr-sm"
                  }`}
                >
                  <div
                    className={`flex items-center gap-1.5 mb-1 text-xs ${
                      role === "bot"
                        ? "text-[#9e68ba]"
                        : role === "owner"
                          ? "text-amber-600 dark:text-amber-400"
                          : "text-white/80"
                    }`}
                  >
                    {role === "bot" ? (
                      <Bot size={12} />
                    ) : role === "owner" ? (
                      <User size={12} />
                    ) : (
                      <User size={12} />
                    )}
                    <span className="font-medium">
                      {role === "bot"
                        ? t("foorchat.chats.bot")
                        : role === "owner"
                          ? t("foorchat.chats.owner")
                          : t("foorchat.chats.user")}
                    </span>
                  </div>

                  {isAudio ? (
                    <div className="mt-0.5">
                      {msg.url ? (
                        <VoicePlayer url={msg.url} isUser={role === "user"} />
                      ) : (
                        <span className="text-xs opacity-60">
                          {t("foorchat.chats.no_audio_url")}
                        </span>
                      )}
                    </div>
                  ) : isImage ? (
                    <div className="mt-1">
                      {msg.url ? (
                        <img
                          src={msg.url}
                          alt={t("foorchat.chats.sent_image")}
                          className="max-w-full max-h-64 rounded-xl object-cover cursor-pointer"
                          onClick={() => window.open(msg.url, "_blank")}
                          loading="lazy"
                        />
                      ) : (
                        <span className="text-xs opacity-60">
                          {t("foorchat.chats.no_image_url")}
                        </span>
                      )}
                    </div>
                  ) : (
                    <p
                      className={`text-sm leading-relaxed whitespace-pre-wrap break-words ${
                        role === "bot" || role === "owner"
                          ? "text-gray-800 dark:!text-gray-200"
                          : "text-white"
                      }`}
                    >
                      {text || "—"}
                    </p>
                  )}

                  {extra && (
                    <div className="mt-2 pt-2 border-t border-gray-100 dark:border-[#3c3a3d] space-y-1">
                      {extra.sendImages && (
                        <div className="flex items-center gap-1 text-xs text-[#9e68ba]">
                          <ImageIcon size={12} />
                          <span>{t("foorchat.chats.images_sent")}</span>
                        </div>
                      )}

                      {extra.phone && (
                        <div className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                          <Package size={12} />
                          <span>{t("foorchat.chats.order_created")}</span>
                        </div>
                      )}
                    </div>
                  )}

                  <p
                    className={`text-[10px] mt-1.5 ${
                      isLeftSide ? "text-gray-400" : "text-white/70"
                    }`}
                  >
                    {formatFullTimestamp(msg.timestamp)}
                  </p>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
      </div>
    );
  }

  return (
    <div className="m-3 bg-white dark:!bg-[#181819] rounded-xl shadow-sm border border-[#fbf4ff] dark:!border-[#3c3a3d] overflow-hidden">
      <div className="p-4 border-b border-gray-100 dark:border-[#3c3a3d]">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 min-w-0">
            <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-6 z-10 bg-gradient-to-r from-white dark:from-[#181819] to-transparent" />
            <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-6 z-10 bg-gradient-to-l from-white dark:from-[#181819] to-transparent" />

            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-[#3c3a3d] scrollbar-track-transparent">
              <Filter
                size={16}
                className={`text-[#9e68ba] flex-shrink-0 ${
                  isRTL ? "mr-4" : "ml-4"
                }`}
              />

              <button
                onClick={() => {
                  setSelectedProductId("all");
                  setCurrentPage(1);
                }}
                className={`px-3.5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedProductId === "all"
                    ? "bg-[#9e68ba] text-white shadow-sm"
                    : "bg-gray-100 dark:bg-[#2a2a2c] text-gray-600 dark:text-gray-400 hover:bg-purple-50 dark:hover:bg-[#6e358c]/30"
                }`}
              >
                {t("foorchat.chats.filter_all")}
                <span className="ml-1.5 opacity-70">({chats.length})</span>
              </button>

              {filterAdsIds.map((adsId) => {
                const count = chats.filter((c) => c.adsId === adsId).length;
                const product = adsIdToProduct[adsId];
                const label = product
                  ? `${product.name || t("foorchat.chats.product")} (${adsId})`
                  : adsId;
                return (
                  <button
                    key={adsId}
                    onClick={() => {
                      setSelectedProductId(adsId);
                      setCurrentPage(1);
                    }}
                    className={`px-3.5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                      selectedProductId === adsId
                        ? "bg-[#9e68ba] text-white shadow-sm"
                        : "bg-gray-100 dark:bg-[#2a2a2c] text-gray-600 dark:text-gray-400 hover:bg-purple-50 dark:hover:bg-[#6e358c]/30"
                    }`}
                    title={label}
                  >
                    {label}
                    <span className="ml-1.5 opacity-70">({count})</span>
                  </button>
                );
              })}
            </div>
          </div>

          <button
            onClick={() => fetchChats(true)}
            disabled={refreshing}
            className="flex-shrink-0 p-2.5 rounded-xl border border-gray-200 dark:border-[#3c3a3d] hover:bg-purple-50 dark:hover:bg-[#6e358c]/30 transition-colors"
            title={t("foorchat.chats.refresh")}
          >
            <RefreshCw
              size={18}
              className={`text-[#9e68ba] ${refreshing ? "animate-spin" : ""}`}
            />
          </button>
        </div>
      </div>

      {filteredChats.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
          <div className="w-16 h-16 rounded-full bg-purple-50 dark:bg-[#6e358c]/20 flex items-center justify-center mb-4">
            <MessageCircle size={28} className="text-[#9e68ba]" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-1">
            {t("foorchat.chats.empty_title")}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
            {selectedProductId !== "all"
              ? t("foorchat.chats.empty_filtered")
              : t("foorchat.chats.empty_default")}
          </p>
        </div>
      ) : (
        <>
          <ul className="divide-y divide-gray-100 dark:divide-[#3c3a3d]">
            {paginatedChats.map((chat) => {
              const id = getChatId(chat);
              return (
                <li key={id}>
                  <button
                    onClick={() => setActiveChatId(id)}
                    className="w-full flex items-start gap-3 px-4 py-3.5 text-left hover:bg-[#fcf6ff] dark:hover:bg-[#222224] transition-colors"
                  >
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#9e68ba] to-[#bc7ade] flex items-center justify-center flex-shrink-0 shadow-sm">
                      <MessageCircle size={20} className="text-white" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex flex-col min-w-0">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <Package
                              size={14}
                              className="text-[#9e68ba] flex-shrink-0"
                            />
                            <span className="font-semibold text-gray-800 dark:text-gray-200 truncate text-sm">
                              {getProductName(chat)}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                            {chat.userName || t("foorchat.chats.facebook_user")}
                          </span>
                        </div>
                        <span className="text-xs text-gray-400 flex-shrink-0 flex items-center gap-1">
                          <Clock size={12} />
                          {formatTimestamp(chat.updatedAt)}
                        </span>
                      </div>

                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                        {getLastMessagePreview(chat)}
                      </p>

                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-purple-50 dark:bg-[#6e358c]/20 text-[#9e68ba]">
                          {chat.messages?.length || 0}{" "}
                          {t("foorchat.chats.messages")}
                        </span>
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-1 px-4 py-3 border-t border-gray-100 dark:border-[#3c3a3d]">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg text-[#9e68ba] disabled:opacity-30 disabled:cursor-not-allowed hover:bg-purple-50 dark:hover:bg-[#6e358c]/20 transition-colors"
              >
                <ChevronLeft size={18} className={isRTL ? "rotate-180" : ""} />
              </button>

              {getVisiblePageNumbers().map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`min-w-[36px] h-9 rounded-lg text-sm font-medium transition-colors ${
                    currentPage === page
                      ? "bg-[#9e68ba] text-white shadow-sm"
                      : "text-gray-600 dark:text-gray-400 hover:bg-purple-50 dark:hover:bg-[#6e358c]/20"
                  }`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg text-[#9e68ba] disabled:opacity-30 disabled:cursor-not-allowed hover:bg-purple-50 dark:hover:bg-[#6e358c]/20 transition-colors"
              >
                <ChevronRight size={18} className={isRTL ? "rotate-180" : ""} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FoorchatChats;
